import React, { useCallback, useMemo } from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { logButtonClick } from 'features/navigation/utils/analytics';

import { IItem, IPicker } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { ComboSlotType } from 'components/product-detail-v2/product-customization/ComboSlotType';
import { getComboSlotType } from 'components/product-detail-v2/product-customization/utils';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { ClickEventComponentNames } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep, UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';
import { isCombo, isPicker } from 'utils/menu';

import { ProductAddToCartCta } from '../product-add-to-cart-cta/product-add-to-cart-cta';
import { ProductCtaWrapper } from '../product-cta-wrapper';
import { ActionsWrapper } from '../product-cta.styled';
import { getModifierSelections, getSingleOrMultipleChoiceCtaBtnProps } from '../utils';

import { useGetNextComboSlotLabel } from './use-get-next-combo-slot-label';
import { findNextValidComboSlotKey, getNextComboSlotKey } from './utils';

interface IProductPickerCtaProps {
  modifierSelections?: UserSelectionModifierSlotMap;
  selectedItem: IItem | undefined;
}

export const ProductPickerCta = ({ modifierSelections, selectedItem }: IProductPickerCtaProps) => {
  const {
    goToCustomizeProduct,
    goToReviewProduct,
    slotKeyToCustomize,
    currentStep,
    isEdit,
    id,
  } = useProductWizardFlow();

  const { isTakeoverActive } = useAppTakeoverPdp(id);

  const {
    submitCustomizations,
    userSelections,
    comboSlotUpdate,
    pickerAspectsUpdate,
    productWizardFlowSteps,
    singleOrMultipleModifierSelection,
    updateSingleOrMultipleModifierSelection,
    menuObject,
    selectedProduct,
  } = useProductWizardContext();

  const isProductHome = currentStep === ProductWizardStep.ProductHome;
  const isProductCustomization = currentStep === ProductWizardStep.ProductCustomization;

  const comboSlotType = getComboSlotType(selectedItem!);

  const nextComboSlotLabel = useGetNextComboSlotLabel({
    slotKeyToCustomize,
    userSelections,
    selectedProduct,
  });

  const nextComboSlotKey = useMemo(
    () =>
      getNextComboSlotKey({
        slotKeyToCustomize,
        userSelections,
      }),
    [slotKeyToCustomize, userSelections]
  );

  const areAllPickerAspectsUpdated = useMemo(() => {
    if (isPicker(menuObject)) {
      const allPickerAspectIds = menuObject.pickerAspects.map(option => option._id);
      const pickerAspectUpdatesMap = Object.fromEntries(
        pickerAspectsUpdate.map(update => [update.id, update.isDirty])
      );

      return allPickerAspectIds.every(pickerAspectId => pickerAspectUpdatesMap[pickerAspectId]);
    }
    return true;
  }, [menuObject, pickerAspectsUpdate]);

  const logButtonClickEvent = useCallback(() => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: 'Update Picker',
      },
    });
  }, []);

  const navigateToNextStep = useCallback(() => {
    const currentFlowStep = productWizardFlowSteps.find(step => step.currentStep);

    if (currentFlowStep?.type === MenuObjectTypes.PICKER_ASPECT) {
      const pickerAspects = (menuObject as IPicker).pickerAspects;

      const currentFlowStepIndex = pickerAspects.findIndex(
        aspect => aspect._id === currentFlowStep.id
      );
      const nextPickerAspect = pickerAspects.at(currentFlowStepIndex + 1);

      if (nextPickerAspect) {
        return goToCustomizeProduct({
          slotKey: nextPickerAspect._id,
          step: ProductWizardStep.ProductCustomization,
        });
      }
    }

    const nextValidComboSlotKey = findNextValidComboSlotKey(nextComboSlotKey, userSelections);

    // Otherwise, we can start customizing the selected product.
    if (nextValidComboSlotKey) {
      return goToCustomizeProduct({
        slotKey: nextValidComboSlotKey,
        step: ProductWizardStep.ProductCustomization,
      });
    }
    goToReviewProduct();
  }, [
    productWizardFlowSteps,
    nextComboSlotKey,
    goToReviewProduct,
    userSelections,
    menuObject,
    goToCustomizeProduct,
  ]);

  const handlePress = useCallback(() => {
    if (selectedItem && modifierSelections) {
      submitCustomizations({
        modifiers: modifierSelections,
        selectedItem,
        slotKey: slotKeyToCustomize,
      });
    }

    const isSingleOrMultipleModifierCustomization = !!singleOrMultipleModifierSelection?.[
      slotKeyToCustomize
    ];

    if (isSingleOrMultipleModifierCustomization) {
      updateSingleOrMultipleModifierSelection(slotKeyToCustomize, { isSaved: true });
    }

    logButtonClickEvent();
    navigateToNextStep();
  }, [
    selectedItem,
    modifierSelections,
    singleOrMultipleModifierSelection,
    slotKeyToCustomize,
    logButtonClickEvent,
    navigateToNextStep,
    submitCustomizations,
    updateSingleOrMultipleModifierSelection,
  ]);

  const hasComboSlotSelections = useCallback(
    (comboSlotType: ComboSlotType | null): boolean => {
      if (!!selectedItem && !!comboSlotType) {
        const currentComboSlot = productWizardFlowSteps.find(
          step => step.type === MenuObjectTypes.COMBO_SLOT && step.currentStep
        );
        const foundComboSlotCustomizations = comboSlotUpdate.find(
          comboSlotUpdateVal =>
            currentComboSlot?.id === comboSlotUpdateVal.id && !!comboSlotUpdateVal.isDirty
        );
        return !!foundComboSlotCustomizations;
      }

      return true;
    },
    [comboSlotUpdate, productWizardFlowSteps, selectedItem]
  );

  const hasPickerAspectSelection = useCallback((): boolean => {
    if (isProductHome || isProductCustomization) {
      // Find the picker aspect the user is currently customizing.
      const currentPickerAspect = productWizardFlowSteps.find(
        step => step.type === MenuObjectTypes.PICKER_ASPECT && step.currentStep
      );

      // See if the user has previously updated the picker aspect.
      const foundPickerAspectUpdate = pickerAspectsUpdate.find(
        pickerAspectUpdateVal =>
          currentPickerAspect?.id === pickerAspectUpdateVal.id && !!pickerAspectUpdateVal.isDirty
      );

      // If yes, then the user has made a selection and can proceed.
      return !!foundPickerAspectUpdate;
    }

    return true;
  }, [isProductCustomization, isProductHome, pickerAspectsUpdate, productWizardFlowSteps]);

  const hasModifierSelections = useCallback(() => {
    const selections = getModifierSelections({
      isEdit,
      singleOrMultipleModifierSelection,
      modifierSelections,
      slotKeyToCustomize,
    });

    const { disabled, label } = getSingleOrMultipleChoiceCtaBtnProps({
      selections,
      singleOrMultipleModifierSelection,
      slotKeyToCustomize,
      type: menuObject._type,
    });

    return { disabled, label };
  }, [
    isEdit,
    menuObject._type,
    modifierSelections,
    singleOrMultipleModifierSelection,
    slotKeyToCustomize,
  ]);

  // If the Selected Product is an item and does not have available options to customize,
  // or if the Selecteed Product does not have options to customize,
  // then we are able to add the product directly to the cart.
  const selectedProductHasNoOptionsSelection =
    (selectedProduct._type === MenuObjectTypes.ITEM &&
      !ProductWizardUtils.IsCustomizable(selectedProduct)) ||
    !selectedProduct.options.length;

  const getCanAddToCart = useMemo(() => {
    return (hasMultiplePickerAspects: boolean) => {
      // Never show Add to Cart during customization step or if there are multiple picker aspects
      if (isProductCustomization || hasMultiplePickerAspects) {
        return false;
      }

      // For pickers, only show Add to Cart on Product Home when:
      // 1. We're on the Product Home step
      // 2. All picker aspects are updated (if there are multiple aspects)
      // 3. Selected product needs no further customization
      return (
        isProductHome &&
        (!hasMultiplePickerAspects || areAllPickerAspectsUpdated) &&
        selectedProductHasNoOptionsSelection
      );
    };
  }, [
    isProductCustomization,
    isProductHome,
    areAllPickerAspectsUpdated,
    selectedProductHasNoOptionsSelection,
  ]);

  const defaultLabel =
    isProductHome && areAllPickerAspectsUpdated ? t('nextCustomize') : nextComboSlotLabel;

  const { disabledCta, labelCta } = useMemo(() => {
    const initialCtaState = { disabledCta: false, labelCta: '' };

    // We only want to run this check for ProductCustomization and ProductHome steps.
    if (!isProductCustomization && !isProductHome) {
      return initialCtaState;
    }

    // Handle scenario when no item is selected.
    if (!selectedItem) {
      return {
        disabledCta: !hasPickerAspectSelection(),
        labelCta:
          selectedProductHasNoOptionsSelection && isProductCustomization
            ? isCombo(selectedProduct)
              ? t('nextReviewYourMeal')
              : t('nextReviewYourSelection')
            : initialCtaState.labelCta,
      };
    }

    // Check if combo slot has customizations.
    const comboSlot = userSelections.comboSlot?.get(slotKeyToCustomize)?.comboSlot;
    const comboSlotOptions = comboSlot?.options || [];

    const hasUnsavedComboSlotCustomizations = comboSlotUpdate.some(
      update => update.id === slotKeyToCustomize && update.isDirty
    );

    if (comboSlotOptions.length > 1 && !hasUnsavedComboSlotCustomizations) {
      return { disabledCta: true, labelCta: '' };
    }

    // Validate modifier selections
    const { disabled: modifierDisabled, label: modifierLabel } = selectedItem?.options?.length
      ? hasModifierSelections()
      : { disabled: false, label: '' };

    // Validate combo slot selections
    const comboSlotValid = hasComboSlotSelections(comboSlotType);

    return {
      disabledCta: modifierDisabled || (!comboSlotType && !comboSlotValid),
      labelCta: modifierLabel || initialCtaState.labelCta,
    };
  }, [
    comboSlotType,
    comboSlotUpdate,
    hasComboSlotSelections,
    hasModifierSelections,
    hasPickerAspectSelection,
    isProductCustomization,
    isProductHome,
    selectedItem,
    selectedProduct,
    selectedProductHasNoOptionsSelection,
    slotKeyToCustomize,
    userSelections.comboSlot,
  ]);

  const hasMultiplePickerAspects = isPicker(menuObject) && menuObject.pickerAspects.length > 1;
  const shouldShowAddToCart = getCanAddToCart(hasMultiplePickerAspects);

  const eventAttributes = {
    currentStep: transformProductWizardStepToEventAttr(currentStep),
    comboSlotType: transformComboSlotTypeToEventAttr(comboSlotType ?? ''),
    Item: selectedItem?.name.locale ?? '',
  };

  const Content = (
    <ActionsWrapper>
      <ActionButton
        disabled={disabledCta}
        flex={1}
        onPress={handlePress}
        testID="customization-submit-button"
        eventAttributes={eventAttributes}
        variant={
          isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
        }
      >
        {labelCta || defaultLabel}
      </ActionButton>
    </ActionsWrapper>
  );

  return shouldShowAddToCart ? (
    <ProductAddToCartCta disableCta={disabledCta} modifierSelections={modifierSelections} />
  ) : (
    <ProductCtaWrapper elements={[{ element: Content }]} />
  );
};
